/* eslint-disable react-hooks/static-components -- inner components close over parent state/props, extracting changes reconciliation */
import { useContext, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import {
  IconPlus,
  IconExchange,
  IconBellOutline,
  IconMultipleUsersFilled,
  IconMultipleUsersOutline,
  IconUserCog,
} from '@iwoca/icons';
import { Button, Pill } from '@iwoca/orion';
import { clsx } from 'clsx';

import { HelpModal } from './components/TelephoneNavItem/HelpModal';
import { baseLinks, secondaryLinks, TMenuLink } from './items/items';
import { NameTag } from './NameTag/NameTag';
import { ActivationContext } from '../../activation/Activation';
import {
  useGetContext,
  useGetSellerWhatsNew,
  useGetSharedFunctionality,
} from '../../api/lending/lapiHooks';
import { buildQueryString } from '../../Buyer/utils/queryParams';
import { useAccountNavigation } from '../../hooks/useAccountNavigation';
import { useSafeNavigate } from '../../hooks/useSafeNavigate';
import { useSellerTrackingProperties } from '../../hooks/useSellerTrackingProperties';
import { useUserGroups } from '../../hooks/useUserGroups';
import { ValidRoutePaths } from '../../routing/utils';
import { useGetSellerSpendingLimitRequest } from '../../Seller/SpendingLimitRequests/hooks/useGetSellerSpendingLimitRequest.hook';
import { SafeLink } from '../SafeLink/SafeLink';
import iwocaPayLogo from '../svg/iwocaPayLogo.svg';

export type TMenuIcon = {
  normal: React.ReactNode;
  selected: React.ReactNode;
};

type TExternalLink = {
  href: string;
  newTab: boolean;
};

export const CreatePayLinkButton = () => {
  const { navigate } = useSafeNavigate();
  return (
    <Button
      className="my-xl !h-[36px] w-full !text-s !font-bold m:!h-auto"
      onClick={() =>
        navigate({ to: '/pay/pay-links/universal', keepParams: true })
      }
    >
      Create Pay Link
      <IconPlus className="min-w-[20px]" />
    </Button>
  );
};

export const SellerNavBar = ({
  hideSideBar,
  onOpenWhatsNew,
}: {
  hideSideBar: () => void;
  onOpenWhatsNew: () => void;
}) => {
  const {
    context: { isLoggedIn },
  } = useGetContext();
  const { isOnboarded } = useContext(ActivationContext);
  const { navigate } = useSafeNavigate();
  const { isOwner, isStaff, isAdmin, isContributor } = useUserGroups();
  const { spendingLimitRequests } = useGetSellerSpendingLimitRequest();
  const [helpModalVisible, setHelpModalVisible] = useState<boolean>(false);
  const { unreadCount } = useGetSellerWhatsNew();
  const sellerTrackingProperties = useSellerTrackingProperties();
  const { functionality } = useGetSharedFunctionality({
    functionalities: [
      'is_shared_spending_limit_enabled',
      'can_access_seller_fee_dashboard',
    ],
  });
  const { navigateToAccount } = useAccountNavigation();

  let baseMenuItems = baseLinks;

  if (!functionality?.is_shared_spending_limit_enabled) {
    baseMenuItems = baseMenuItems.filter(
      (menuItem) => menuItem.title !== 'Customers',
    );
  }

  if (functionality?.is_shared_spending_limit_enabled) {
    baseMenuItems = baseMenuItems.filter(
      (menuItem) => menuItem.title !== 'Spending Limits',
    );
  }

  if (spendingLimitRequests.length === 0) {
    baseMenuItems = baseMenuItems.filter(
      (menuItem) => menuItem.title !== 'Spending Limits',
    );
  }

  if (!functionality?.can_access_seller_fee_dashboard) {
    baseMenuItems = baseMenuItems.filter(
      (menuItem) => menuItem.title !== 'Fees',
    );
  }

  if (isContributor) {
    baseMenuItems = baseMenuItems.filter(
      (menuItem) => menuItem.title !== 'Integrations',
    );
  }

  let secondaryMenuItems = secondaryLinks;
  if (!isOnboarded) secondaryMenuItems = [];

  const LoggedInItems = () => {
    return (
      <nav
        className="sticky left-0 top-0 box-border grid h-screen grid-cols-[100%] grid-rows-[min-content] gap-m overflow-y-auto px-l py-xl text-left m:h-[calc(100vh-60px)] [&_a]:no-underline"
        aria-label="Main Menu"
      >
        <div>
          <SafeLink to={'/pay/dashboard'}>
            <img
              src={iwocaPayLogo}
              className="mb-s w-[111px] m:hidden"
              alt="iwocaPay logo"
              aria-hidden="true"
            />
          </SafeLink>
          <div className="mt-m w-full">
            <NameTag />
          </div>

          {isOnboarded ? (
            <Button
              className="my-xl !h-[36px] w-full !text-s !font-bold m:!h-auto"
              onClick={() => {
                navigate({ to: '/pay/pay-links/universal', keepParams: true });
                hideSideBar();
              }}
            >
              Create Pay Link
              <IconPlus className="min-w-[20px]" />
            </Button>
          ) : (
            <Button
              className="my-xl !h-[36px] w-full !text-s !font-bold m:!h-auto"
              onClick={() => {
                navigate({ to: '/pay/activation', keepParams: true });
                hideSideBar();
              }}
            >
              Get started
            </Button>
          )}

          <div className="mt-0 grid w-full grid-cols-[100%] gap-xs">
            {baseMenuItems.map((menuItem, index) => (
              <MenuItem
                key={index}
                title={menuItem.title}
                internal_path={menuItem.internal_path}
                icon={menuItem.icon}
                onClick={() => hideSideBar()}
                promotional_badge={menuItem.promotional_badge}
              />
            ))}
          </div>
        </div>
        <div className="self-end">
          <div className="grid grid-cols-[100%] gap-s">
            {(isStaff || isOwner) && (
              <MenuItem
                title="Loans dashboard"
                icon={{
                  normal: <IconExchange />,
                  selected: <IconExchange />,
                }}
                compact={true}
                onClick={() => {
                  tracking.ampli.switchedToCoreBusinessDashboard();
                  navigateToAccount(buildQueryString());
                }}
              />
            )}
            <MenuItem
              title="What's new"
              icon={{
                normal: <IconBellOutline />,
                selected: <IconBellOutline />,
              }}
              onClick={() => {
                tracking.ampli.whatsNewMenuItemClicked(
                  sellerTrackingProperties,
                );
                onOpenWhatsNew();
                hideSideBar();
              }}
              showDot={unreadCount > 0}
              compact={true}
            />
            <hr className="h-[1px] w-full border-none bg-secondary-80" />
            {secondaryMenuItems.map((menuItem, index) => (
              <MenuItem
                key={index}
                title={menuItem.title}
                external_link={menuItem.external_link}
                internal_path={menuItem.internal_path}
                onClick={() => hideSideBar()}
                icon={menuItem.icon}
                compact={true}
              />
            ))}
            {(isStaff || isOwner || isAdmin) && (
              <MenuItem
                title="Manage users"
                internal_path="/pay/manage-users"
                icon={{
                  normal: <IconMultipleUsersOutline />,
                  selected: <IconMultipleUsersFilled />,
                }}
                onClick={() => hideSideBar()}
                compact={true}
              />
            )}
            <MenuItem
              title="Manage account"
              internal_path="/pay/manage-account"
              icon={{
                normal: <IconUserCog />,
                selected: <IconUserCog />,
              }}
              onClick={() => hideSideBar()}
              compact={true}
            />
            <hr className="h-[1px] w-full border-none bg-secondary-80" />
            <MenuItem
              title="FAQs"
              external_link={{
                href: 'https://support.iwoca.co.uk/en/collections/2145896-what-s-iwocapay',
                newTab: true,
              }}
              compact={true}
            />
            <MenuItem
              title="Help"
              onClick={() => {
                setHelpModalVisible(true);
              }}
              compact={true}
            />
            <MenuItem
              title="Logout"
              external_link={{ href: '/my_account/logout/', newTab: false }}
              compact={true}
            />
          </div>
        </div>
      </nav>
    );
  };

  const NotLoggedInItems = () => {
    return (
      <nav
        className="sticky left-0 top-0 box-border grid h-screen grid-cols-[100%] grid-rows-[min-content] gap-m overflow-y-auto px-l py-xl text-left m:h-[calc(100vh-60px)] [&_a]:no-underline"
        aria-label="Main Menu"
      >
        <div>
          <SafeLink to={'/pay/dashboard'}>
            <img
              src={iwocaPayLogo}
              className="mb-s w-[111px] m:hidden"
              alt="iwocaPay logo"
              aria-hidden="true"
            />
          </SafeLink>
        </div>
        <div className="self-end">
          <div className="grid grid-cols-[100%] gap-s">
            <hr className="h-[1px] w-full border-none bg-secondary-80" />
            <MenuItem
              title="FAQs"
              external_link={{
                href: 'https://support.iwoca.co.uk/en/collections/2145896-what-s-iwocapay',
                newTab: true,
              }}
              compact={true}
            />
            <MenuItem
              title="Help"
              onClick={() => {
                setHelpModalVisible(true);
              }}
              compact={true}
            />
            <MenuItem
              title="Logout"
              external_link={{ href: '/my_account/logout/', newTab: false }}
              compact={true}
            />
          </div>
        </div>
      </nav>
    );
  };

  return (
    <>
      <HelpModal
        isOpen={helpModalVisible}
        closeModal={() => setHelpModalVisible(false)}
      />
      {isLoggedIn ? <LoggedInItems /> : <NotLoggedInItems />}
    </>
  );
};

const MenuItem = ({
  title,
  internal_path,
  external_link,
  onClick,
  compact = false,
  icon = null,
  promotional_badge,
  showDot = false,
}: {
  title: string;
  internal_path?: ValidRoutePaths;
  external_link?: TExternalLink;
  onClick?: () => void;
  compact?: boolean;
  icon?: TMenuIcon | null;
  promotional_badge?: TMenuLink['promotional_badge'];
  showDot?: boolean;
}): React.ReactNode => {
  const isSelected: boolean = window.location.pathname.startsWith(
    internal_path as string,
  );

  const PromotionalPill = ({
    promotional_badge,
  }: {
    promotional_badge: TMenuLink['promotional_badge'];
  }) => {
    if (!promotional_badge) return <></>;

    const todayDate = new Date();
    const expiryDate = new Date(promotional_badge.launch_date);
    expiryDate.setDate(
      promotional_badge.launch_date.getDate() + promotional_badge.expiry_days,
    );

    if (todayDate < promotional_badge?.launch_date) return <></>;
    if (todayDate >= expiryDate) return <></>;

    return (
      <Pill variant="red" size="small">
        New
      </Pill>
    );
  };

  const ButtonComponent = () => {
    return (
      <div
        className={clsx(
          'grid grid-cols-[15px_1fr_max-content] items-center gap-s rounded-s border-none text-left text-s font-med text-secondary-20 transition-all duration-[70ms] ease-out',
          !compact && 'py-m',
          {
            'bg-secondary-90 pl-s hover:bg-secondary-90': isSelected,
            'bg-transparent': !isSelected,
            'w-full py-xs hover:pl-s': compact,
          },
          !isSelected &&
            'hover:cursor-pointer hover:bg-secondary-97 hover:pl-s',
        )}
      >
        {icon && (isSelected ? icon.selected : icon.normal)}
        <span className="flex items-center gap-xs whitespace-nowrap">
          {title}
          {showDot && (
            <span
              data-testid="whatsNewNavDot"
              aria-label="New notifications"
              className="h-[6px] w-[6px] shrink-0 -translate-y-[3px] rounded-full bg-core-40"
            />
          )}
        </span>
        <PromotionalPill promotional_badge={promotional_badge} />
      </div>
    );
  };

  if (external_link) {
    return (
      <a href={external_link.href} target="_blank" rel="noopener noreferrer">
        <ButtonComponent />
      </a>
    );
  }

  if (internal_path && onClick) {
    return (
      <SafeLink onClick={onClick} to={internal_path}>
        <ButtonComponent />
      </SafeLink>
    );
  }

  if (internal_path) {
    return (
      <SafeLink to={internal_path}>
        <ButtonComponent />
      </SafeLink>
    );
  }

  if (onClick) {
    return (
      <button onClick={onClick} className="m-0 border-0 bg-transparent p-0">
        <ButtonComponent />
      </button>
    );
  }

  return <ButtonComponent />;
};
